import {FlowComponentsPanelState} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {IMutableGridModel} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridModel';
import {CellSettingsBarState} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/CellSettingsBarState';
import {FlowPropertiesPanelState} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/FlowPropertiesPanelState';
import {PanelLayoutState} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/PanelState';
import {action, observable} from 'mobx';
import {IVisualDesignerLayoutState, IWorkbenchDesignUIState} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignUIState';

export class VisualDesignerTab
{
    public static readonly FlowTab:VisualDesignerTab = new VisualDesignerTab('flow');
    public static readonly SurfaceTab:VisualDesignerTab = new VisualDesignerTab('surface');

    private constructor(private _value:string)
    {
    }

    isEqual(other:VisualDesignerTab):boolean
    {
        return other && other._value === this._value;
    }

    value():string
    {
        return this._value;
    }

    toUrlPathPart():string
    {
        return this._value;
    }
}

export class ConsolePanelState
{
    public consoleList:IMutableGridModel;
}

export class ErrorsPanelState
{
    public errorsList:IMutableGridModel;
}

export class FlowPanelsState
{
    components:FlowComponentsPanelState = new FlowComponentsPanelState();
    properties:FlowPropertiesPanelState = new FlowPropertiesPanelState();
}

export class PanelsState
{
    flow:FlowPanelsState = new FlowPanelsState();
    console:ConsolePanelState = new ConsolePanelState();
    errors:ErrorsPanelState = new ErrorsPanelState();
}

export class FullScreenState
{
    @observable
    public isFullScreen = false;

    public pageDomElement:HTMLElement = null;
}

export class VisualDesignerLayoutState implements IVisualDesignerLayoutState
{
    @observable
    public gridDomRef:HTMLElement | null = null;

    @observable
    public gridLeftPanelsDomRef:HTMLElement | null = null;

    public gridGlassDomRef:HTMLElement | null = null;

    @observable
    public gridRightPanelsDomRef:HTMLElement | null = null;

    @observable
    public topBarLeftDomRef:HTMLElement | null = null;

    @observable
    public topBarMiddleDomRef:HTMLElement | null = null;

    @observable
    public topBarMiddleZoomDomRef:HTMLElement | null = null;

    @observable
    public topBarRightDomRef:HTMLElement | null = null;

    @observable
    public wonderBarDomRef:HTMLElement | null = null;

    @observable
    public gridLeftColumnWidthPx:number = 325; // in pixels

    @observable
    public gridRightColumnWidth:number = 38.2; // in percent

    @observable
    public frameDetailsContainerHeight:number = 50; // in percent

    @observable
    public isSurfaceCopyPagesPanelShown:boolean = false;

    @observable
    public isSurfacePagesOutlinePanelCollapsed:boolean = false;

    @observable
    public isSurfaceComponentsPanelCollapsed:boolean = false;

    @observable
    public isSurfaceBoxesOutlinePanelCollapsed:boolean = false;

    @observable
    public surfaceCopyPagesPanelHeight:number = 0; // in percent

    @observable
    public surfaceCopyPagesPanelExpandedPartHeightPx:number = 0; // in pixels

    @observable
    public surfacePagesOutlinePanelHeight:number = 20; // in percent

    @observable
    public surfaceComponentsPanelHeight:number = 38; // in percent

    @observable
    public surfaceBoxesOutlinePanelHeight:number = 42; // in percent

    public onCopyPagesPanelShow:() => void = null;
    public onCopyPagesPanelHide:() => void = null;
    public onCopyPagesPanelExpandedPartHeightChange:(deltaPx:number) => void = null;

    public toDesignUIState():IVisualDesignerLayoutState
    {
        return {
            gridLeftColumnWidthPx: this.gridLeftColumnWidthPx,
            gridRightColumnWidth: this.gridRightColumnWidth,
            frameDetailsContainerHeight: this.frameDetailsContainerHeight,
            isSurfaceCopyPagesPanelShown: this.isSurfaceCopyPagesPanelShown,
            isSurfacePagesOutlinePanelCollapsed: this.isSurfacePagesOutlinePanelCollapsed,
            isSurfaceComponentsPanelCollapsed: this.isSurfaceComponentsPanelCollapsed,
            isSurfaceBoxesOutlinePanelCollapsed: this.isSurfaceBoxesOutlinePanelCollapsed,
            surfaceCopyPagesPanelHeight: this.surfaceCopyPagesPanelHeight,
            surfaceCopyPagesPanelExpandedPartHeightPx: this.surfaceCopyPagesPanelExpandedPartHeightPx,
            surfacePagesOutlinePanelHeight: this.surfacePagesOutlinePanelHeight,
            surfaceComponentsPanelHeight: this.surfaceComponentsPanelHeight,
            surfaceBoxesOutlinePanelHeight: this.surfaceBoxesOutlinePanelHeight,
        }
    }

    public fromDesignUIState(state:IVisualDesignerLayoutState):void
    {
        this.gridLeftColumnWidthPx = state.gridLeftColumnWidthPx;
        this.gridRightColumnWidth = state.gridRightColumnWidth;
        this.frameDetailsContainerHeight = state.frameDetailsContainerHeight;
        this.isSurfaceCopyPagesPanelShown = state.isSurfaceCopyPagesPanelShown;
        this.isSurfacePagesOutlinePanelCollapsed = state.isSurfacePagesOutlinePanelCollapsed;
        this.isSurfaceComponentsPanelCollapsed = state.isSurfaceComponentsPanelCollapsed;
        this.isSurfaceBoxesOutlinePanelCollapsed = state.isSurfaceBoxesOutlinePanelCollapsed;
        this.surfaceCopyPagesPanelHeight = state.surfaceCopyPagesPanelHeight;
        this.surfaceCopyPagesPanelExpandedPartHeightPx = state.surfaceCopyPagesPanelExpandedPartHeightPx;
        this.surfacePagesOutlinePanelHeight = state.surfacePagesOutlinePanelHeight;
        this.surfaceComponentsPanelHeight = state.surfaceComponentsPanelHeight;
        this.surfaceBoxesOutlinePanelHeight = state.surfaceBoxesOutlinePanelHeight;
    }
}

export class WorkbenchVisibilityState
{
    @observable
    public isSpreadsheetVisible:boolean = true;

    @observable
    public isSpreadsheetFullScreen:boolean = false;

    @observable
    public isPropertiesPanelVisible:boolean = false;

    @observable
    public isPagesPanelVisible:boolean = false;

    // todo: move into different place after refactoring of whole state structure is done
    public layout:VisualDesignerLayoutState = new VisualDesignerLayoutState();
}

export enum FocusedArea
{
    None = 'None',
    Surface = 'Surface',
    Spreadsheet = 'Spreadsheet',
}

export class WorkbenchState implements IWorkbenchDesignUIState
{
    @observable
    public selectedTab:VisualDesignerTab = null;

    @observable
    public focusedArea:FocusedArea = FocusedArea.None;

    public readonly layout:PanelLayoutState = new PanelLayoutState();
    public readonly panels:PanelsState = new PanelsState();
    public readonly fullScreen:FullScreenState = new FullScreenState();
    public readonly visibility:WorkbenchVisibilityState = new WorkbenchVisibilityState();
    public readonly cellSettingsBar:CellSettingsBarState = new CellSettingsBarState();

    public toDesignUIState():IWorkbenchDesignUIState
    {
        return {
            focusedArea: this.focusedArea,
            visibility: {
                isSpreadsheetVisible: this.visibility.isSpreadsheetVisible,
                isSpreadsheetFullScreen: this.visibility.isSpreadsheetFullScreen,
                isPropertiesPanelVisible: this.visibility.isPropertiesPanelVisible,
                isPagesPanelVisible: this.visibility.isPagesPanelVisible,
                layout: this.visibility.layout.toDesignUIState()
            },
            cellSettingsBar: {
                isBarVisible: this.cellSettingsBar.isBarVisible
            }
        }
    }

    @action
    public fromDesignUIState(state:IWorkbenchDesignUIState)
    {
        this.focusedArea = state.focusedArea;
        this.visibility.isSpreadsheetVisible = state.visibility.isSpreadsheetVisible;
        this.visibility.isSpreadsheetFullScreen = state.visibility.isSpreadsheetFullScreen;
        this.visibility.isPropertiesPanelVisible = state.visibility.isPropertiesPanelVisible;
        this.visibility.isPagesPanelVisible = state.visibility.isPagesPanelVisible;
        this.visibility.layout.fromDesignUIState(state.visibility.layout);
        this.cellSettingsBar.isBarVisible = state.cellSettingsBar.isBarVisible;
    }
}
